import { useQuery } from '@tanstack/react-query';
import { contentfulGraphQLRequest } from '../services/contentful/contentful-client';
import { GetFaqsQuery } from '../graphql/generated/types';
import { FAQItem } from '../components/help/FAQComponent';

// GraphQL query string without category filter and without locale
const GET_FAQS_QUERY_NO_LOCALE = `
  query GetFaqs($limit: Int, $skip: Int) {
    faqCollection(limit: $limit, skip: $skip) {
      total
      skip
      limit
      items {
        sys {
          id
          publishedAt
        }
        question
        category
        response {
          json
        }
      }
    }
  }
`;

// GraphQL query string without category filter but with locale
const GET_FAQS_QUERY = `
  query GetFaqs($locale: String, $limit: Int, $skip: Int) {
    faqCollection(locale: $locale, limit: $limit, skip: $skip) {
      total
      skip
      limit
      items {
        sys {
          id
          publishedAt
        }
        question
        category
        response {
          json
        }
      }
    }
  }
`;

// GraphQL query string with category filter but without locale
const GET_FAQS_WITH_CATEGORY_QUERY_NO_LOCALE = `
  query GetFaqs($limit: Int, $skip: Int, $category: String) {
    faqCollection(limit: $limit, skip: $skip, where: { category: $category }) {
      total
      skip
      limit
      items {
        sys {
          id
          publishedAt
        }
        question
        category
        response {
          json
        }
      }
    }
  }
`;

// GraphQL query string with category filter and locale
const GET_FAQS_WITH_CATEGORY_QUERY = `
  query GetFaqs($locale: String, $limit: Int, $skip: Int, $category: String) {
    faqCollection(locale: $locale, limit: $limit, skip: $skip, where: { category: $category }) {
      total
      skip
      limit
      items {
        sys {
          id
          publishedAt
        }
        question
        category
        response {
          json
        }
      }
    }
  }
`;

interface UseGetFAQsParams {
  category?: string;
  limit?: number;
  skip?: number;
  enabled?: boolean;
}

/**
 * Hook to fetch FAQs from Contentful
 * Returns empty array if Contentful is unavailable
 */
export const useGetFAQs = ({
  category,
  limit = 100,
  skip = 0,
  enabled = true,
}: UseGetFAQsParams = {}) => {
  // For now, don't pass locale to use Contentful's default locale
  // This avoids the error "Requested locale 'fr' does not exist in the space"
  // TODO: Map i18n language codes to Contentful locale codes once locales are configured
  // You can enable this later by:
  // const { i18n } = useTranslation();
  // const currentLocale = i18n.language || null;
  const currentLocale: string | null = null; // Use Contentful default locale

  const {
    data: faqs,
    isLoading: isFetchingFAQs,
    error: faqsError,
    refetch,
  } = useQuery<FAQItem[], Error>({
    queryKey: ['faqs', currentLocale, category, limit, skip],
    queryFn: async (): Promise<FAQItem[]> => {
      try {
        // Build variables - use different query based on whether category and locale are provided
        const variables: any = {
          limit,
          skip,
        };

        // Add locale only if provided
        if (currentLocale) {
          variables.locale = currentLocale;
        }

        // Use appropriate query based on whether category filter and locale are needed
        let queryToUse: string;
        if (category && currentLocale) {
          queryToUse = GET_FAQS_WITH_CATEGORY_QUERY;
          variables.category = category;
        } else if (category && !currentLocale) {
          queryToUse = GET_FAQS_WITH_CATEGORY_QUERY_NO_LOCALE;
          variables.category = category;
        } else if (!category && currentLocale) {
          queryToUse = GET_FAQS_QUERY;
        } else {
          queryToUse = GET_FAQS_QUERY_NO_LOCALE;
        }

        const response = await contentfulGraphQLRequest<GetFaqsQuery>(
          queryToUse,
          variables,
        );

        const faqCollection = response.data?.faqCollection;

        if (!faqCollection || !faqCollection.items) {
          return [];
        }

        // Transform Contentful data to FAQItem format
        type FaqItem = NonNullable<GetFaqsQuery['faqCollection']>['items'][number];

        return faqCollection.items
          .filter((item): item is NonNullable<FaqItem> => item !== null)
          .map((item: NonNullable<FaqItem>) => {
            // Extract plain text from Rich Text JSON if needed
            let answer = '';
            if (item.response?.json) {
              // Simple extraction of text from Rich Text JSON
              // For more complex rendering, use @contentful/rich-text-react-renderer
              answer = extractTextFromRichText(item.response.json);
            }

            return {
              id: item.sys.id,
              question: item.question || '',
              answer: answer || '',
            };
          });
      } catch (error) {
        // Return empty array if Contentful fails
        return [];
      }
    },
    enabled: enabled && !!process.env.NX_CONTENTFUL_SPACE_ID && !!process.env.NX_CONTENTFUL_ACCESS_TOKEN,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    retry: false, // Don't retry on error, return empty array instead
  });

  return {
    faqs: faqs || [],
    isFetchingFAQs,
    faqsError,
    refetch,
  };
};

/**
 * Extract plain text from Contentful Rich Text JSON
 * This is a simple implementation. For complex formatting,
 * use @contentful/rich-text-react-renderer
 */
function extractTextFromRichText(json: any): string {
  if (!json || typeof json !== 'object') {
    return '';
  }

  if (json.nodeType === 'document' && json.content) {
    return json.content
      .map((node: any) => extractTextFromNode(node))
      .join(' ')
      .trim();
  }

  return '';
}

function extractTextFromNode(node: any): string {
  if (!node) return '';

  if (node.nodeType === 'text' && node.value) {
    return node.value;
  }

  if (node.content && Array.isArray(node.content)) {
    return node.content.map((child: any) => extractTextFromNode(child)).join(' ');
  }

  return '';
}
